/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Account } from '@carbon/react/icons';
import {
  CodeSnippet,
  CodeSnippetSkeleton,
  FormLabel,
  TextInput
} from 'carbon-components-react';
import _ from 'lodash';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import { useMount } from 'react-use';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { copyContentToClipboard } from '@ibm-watson/shared/modules/ia/util/clipboard/clipboard';
import { useAppSelector } from '@ibm-watson/shared/store';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useChannelHelpers } from '../../../hooks/useChannelHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  isSubscribeIntegrationLoading,
  selectDraftIntegration
} from '../../../store/channels/selectors';
import {
  CUSTOM_SMS_TYPE,
  GENESYS_SMS_TYPE,
  INTELEPEER_SMS_TYPE,
  TWILIO_SMS_TYPE
} from '../../../util/channelTypes';
import { ChannelContentProps } from '../../../util/types';
import { TWILIO_COLOR_PRIMARY } from '../../../util/validation/constants';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { CustomSMSIcon } from '../../icons/CustomSMSIcon';
import { GenesysIcon } from '../../icons/GenesysIcon';
import { IntelepeerIcon } from '../../icons/IntelepeerIcon';
import { TwilioIcon } from '../../icons/TwilioIcon';
import { sharedMessages } from '../../messages';
import { TwilioAuth } from '../../twilio-auth/TwilioAuth';
import { BasicSetup } from '../basic-setup-tab/messages';
import { links } from '../links';
import { FIELDS } from '../schema';
import { CustomAuth } from '../sms-new/CustomAuth';
import { GenesysAuth } from '../sms-new/GenesysAuth';
import { IntelepeerAuth } from '../sms-new/IntelepeerAuth';
import {
  ConnectProviderStepMessages as messages,
  SMSNew
} from '../sms-new/messages';

export const SMSProviderTab = ({
  assistantId,
  environmentId,
  readOnly
}: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { subscribeForWebhookUrl } = useChannelHelpers(formMethods);
  const { getFieldValue, isInvalidOrEmpty, registerInputField } =
    useFormHelpers(formMethods);

  const draftIntegration = useAppSelector(selectDraftIntegration, shallowEqual);
  const isSubscribeLoading = useAppSelector(isSubscribeIntegrationLoading);

  const phoneNumberChangeState = useState<boolean>(false);

  const selectedProvider = getFieldValue(FIELDS.PROVIDER_TYPE);

  useMount(() => {
    if (
      !isInvalidOrEmpty(FIELDS.PROVIDER_TYPE) &&
      isInvalidOrEmpty(FIELDS.WEBHOOK_URL) &&
      !_.isNil(assistantId)
    ) {
      const data = _.isEqual(selectedProvider, INTELEPEER_SMS_TYPE)
        ? {
            account_secret: getFieldValue(FIELDS.INTELEPEER_ACCOUNT_SECRET),
            auth_token: getFieldValue(FIELDS.INTELEPEER_AUTHENTICATION_TOKEN),
            phone_number: getFieldValue(FIELDS.FROM_PHONE_NUMBER)
          }
        : {
            account_sid: getFieldValue(FIELDS.TWILIO_ACCOUNT_SID),
            auth_token: getFieldValue(FIELDS.TWILIO_AUTHENTICATION_TOKEN)
          };

      subscribeForWebhookUrl(
        assistantId,
        environmentId,
        draftIntegration,
        data,
        FIELDS.WEBHOOK_URL,
        getFieldValue(FIELDS.PROVIDER_TYPE)
      );
    }
  });

  return (
    <TabContent
      ImageComponent={() => <Account size={32} />}
      title={<FormattedMessage {...messages.tabTitle} />}>
      <div className="TabContent__body-content">
        <div className="TabContent__subtitle TabContent__link-larger-text">
          <FormattedMessageWithLink
            {...messages.description}
            url={
              useIsNewIA()
                ? links.learnMoreDeploySmsNewIA
                : links.learnMoreDeploySmsLink
            }
          />
        </div>
        <div className="TabContent__spacer32" />
        {selectedProvider === TWILIO_SMS_TYPE && (
          <>
            <TwilioIcon
              className="ServiceDesk--twilio"
              channelType="sms"
              fill={TWILIO_COLOR_PRIMARY}
            />
            <h4 className="productive-heading-02">
              <FormattedMessage
                {...messages.connectAccount}
                values={{ provider: selectedProvider }}
              />
            </h4>
            <div className="TabContent__spacer16" />
            <div className="TabContent__subtitle">
              <FormattedMessageWithLink
                {...messages.descriptionTwilio}
                url={links.twilioAccountLink}
              />
            </div>
            <div className="TabContent__spacer32" />
            <TwilioAuth
              accountSidField={FIELDS.TWILIO_ACCOUNT_SID}
              authTokenField={FIELDS.TWILIO_AUTHENTICATION_TOKEN}
              readOnly={readOnly}
            />
          </>
        )}
        {selectedProvider === INTELEPEER_SMS_TYPE && (
          <>
            <IntelepeerIcon
              className="ServiceDesk--intelepeer"
              fill={TWILIO_COLOR_PRIMARY}
            />
            <div className="TabContent__spacer32" />
            <h4 className="productive-heading-02">
              <FormattedMessage
                {...messages.connectAccount}
                values={{ provider: selectedProvider }}
              />
            </h4>
            <div className="TabContent__spacer16" />
            <div className="TabContent__subtitle">
              <FormattedMessageWithLink
                {...messages.descriptionIntelepeer}
                url={links.intelepeerAccountLink}
              />
            </div>
            <div className="TabContent__spacer32" />
            <IntelepeerAuth readOnly={readOnly} />
          </>
        )}
        {selectedProvider === GENESYS_SMS_TYPE && (
          <>
            <GenesysIcon className="ServiceDesk--genesys" />
            <div className="TabContent__spacer32" />
            <h4 className="productive-heading-02">
              <FormattedMessage
                {...messages.connectAccount}
                values={{ provider: selectedProvider }}
              />
            </h4>
            <div className="TabContent__spacer16" />
            <div className="TabContent__subtitle">
              <FormattedMessageWithLink
                {...messages.descriptionGenesys}
                url={links.genesysAccountLink}
              />
            </div>
            <div className="TabContent__spacer32" />
            <GenesysAuth readOnly={readOnly} />
          </>
        )}
        {selectedProvider === CUSTOM_SMS_TYPE && (
          <>
            <CustomSMSIcon />
            <div className="TabContent__spacer32" />
            <h4 className="productive-heading-02">
              {t(messages.connectBYOPAccount)}
            </h4>
            <div className="TabContent__spacer16" />
            <div className="TabContent__subtitle">
              <FormattedMessageWithLink
                {...messages.descriptionCustom}
                url={links.genesysAccountLink}
              />
            </div>
            <div className="TabContent__spacer32" />
            <CustomAuth readOnly={readOnly} />
          </>
        )}
        <div className="TabContent__field-container SMS__providerInfo">
          <FormLabel className="TabContent__field-label">
            <FormattedMessage {...SMSNew.WebhookStep} />
          </FormLabel>
          {_.isNil(draftIntegration?.integration_id) || isSubscribeLoading ? (
            <CodeSnippetSkeleton />
          ) : (
            <CodeSnippet
              copyButtonDescription={t(sharedMessages.copyToClipboard)}
              copyLabel={t(sharedMessages.copyToClipboard)}
              feedback={t(sharedMessages.copied)}
              onClick={() => {
                copyContentToClipboard(getFieldValue(FIELDS.WEBHOOK_URL));
              }}
              type="single">
              {getFieldValue(FIELDS.WEBHOOK_URL)}
            </CodeSnippet>
          )}
          <div className="TabContent__spacer32" />
          <TextInput
            {...registerInputField({
              field: FIELDS.FROM_PHONE_NUMBER,
              isPasswordField: false,
              labelText: SMSNew.PhoneNumberStep,
              placeholderText: BasicSetup.phonePlaceholder,
              changeState: phoneNumberChangeState,
              readOnly
            })}
          />
          <div className="TabContent__spacer32" />
        </div>
      </div>
    </TabContent>
  );
};
